import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom, of } from 'rxjs';
import { environment } from 'environments/environment';
import { map, tap } from 'rxjs/operators';
import { SegmentView, SubSegmentsByGLStringResultItem } from 'app/models/form/SubSegmentsByGLStringsBatch';
import { BatchCacheLoader } from 'app/shared/utils/batch-cache-loader';

@Injectable({
	providedIn: 'root',
})
export class SubSegmentService {
	private readonly urlPath = 'sub-segment';

	// Per-client DataLoader that coalesces same-tick sub-segments-by-GL-string lookups into one batch
	// request and memoizes by GL string for the page lifecycle. Segmentation config is stable within an
	// editing session, so memoizing is safe and collapses the per-row N+1.
	private readonly subSegmentLoaders = new Map<string, BatchCacheLoader<SegmentView[]>>();

	constructor(private httpClient: HttpClient) {}

	private getSubSegmentLoader(clientId: string): BatchCacheLoader<SegmentView[]> {
		let loader = this.subSegmentLoaders.get(clientId);
		if (!loader) {
			loader = new BatchCacheLoader<SegmentView[]>(
				(glStrings) =>
					firstValueFrom(
						this.getSubSegmentsByGLStrings(clientId, glStrings).pipe(
							map((items) => new Map(items.map((item) => [item.key, item.segments])))
						)
					),
				// A batch omission resolves like the single-item endpoint's "not found": empty.
				{ defaultForMissingKey: () => [] }
			);
			this.subSegmentLoaders.set(clientId, loader);
		}
		return loader;
	}

	/** Drop the memoized sub-segment lookups (e.g. on client switch or after config edits). */
	clearSubSegmentLookupCache(): void {
		this.subSegmentLoaders.forEach((loader) => loader.clear());
		this.subSegmentLoaders.clear();
	}

	getSubSegments(clientId: string, segmentNumber: string): Observable<any> {
		return this.httpClient.get<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/${segmentNumber}`).pipe();
	}
	getSubSegmentById(clientId: string, id: string): Observable<any> {
		return this.httpClient
			.get<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/getSubSegment/${id}`)
			.pipe();
	}
	createSubSegment(clientId: string, data: any): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}`, data)
			.pipe(tap(() => this.clearSubSegmentLookupCache()));
	}
	updateSubSegment(clientId: string, data: any): Observable<any> {
		return this.httpClient
			.put<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}`, data)
			.pipe(tap(() => this.clearSubSegmentLookupCache()));
	}
	createSubSegmentLinkage(clientId: string, data: any): Observable<any> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/linkage`, data)
			.pipe(tap(() => this.clearSubSegmentLookupCache()));
	}
	getAccountLinkage(clientId: string, segmentId: string): Observable<any> {
		return this.httpClient
			.get<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/getLinkageAccounts/${segmentId}`)
			.pipe();
	}
	deactivateSubSegment(clientId: string, id: string): Observable<any> {
		return this.httpClient
			.delete<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/${id}`)
			.pipe(tap(() => this.clearSubSegmentLookupCache()));
	}
	getActiveSubSegments(clientId: string, segmentId: string): Observable<any> {
		return this.httpClient
			.get<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/getActiveSubSegments/${segmentId}`)
			.pipe();
	}
	deactivateSubSegmentLinkage(clientId: string, id: string): Observable<any> {
		return this.httpClient
			.delete<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/linkage/${id}`)
			.pipe(tap(() => this.clearSubSegmentLookupCache()));
	}
	importSubSegment(clientId: string, data: any): Observable<HttpEvent<any>> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/import-segment-linkage`, data)
			.pipe();
	}
	getSubSegmentsByGeneralLedgerId(clientId: string, generalLedgerId: string): Observable<any> {
		if (!generalLedgerId || generalLedgerId.trim() === '') {
			return of([]);
		}
		// Routed through the batch+cache loader: identical/same-tick lookups coalesce to a single
		// get-sub-segments-by-gl-strings request and repeats are served from cache. The returned segments
		// are byte-identical to the old per-GL GET.
		return this.getSubSegmentLoader(clientId).load$(generalLedgerId.trim());
	}

	// Batch of getSubSegmentsByGeneralLedgerId: resolves sub-segments for many GL strings in one
	// request. Each result item is keyed by its GL string so the caller maps results per row.
	getSubSegmentsByGLStrings(clientId: string, glStrings: string[]): Observable<SubSegmentsByGLStringResultItem[]> {
		return this.httpClient
			.post<SubSegmentsByGLStringResultItem[]>(
				`${environment.apiUrl}${this.urlPath}/clients/${clientId}/get-sub-segments-by-gl-strings`,
				glStrings
			)
			.pipe();
	}
	importSubSegments(clientId: string, data: any): Observable<HttpEvent<any>> {
		return this.httpClient
			.post<any>(`${environment.apiUrl}${this.urlPath}/clients/${clientId}/import-sub-segments`, data)
			.pipe();
	}
	getSegmentationIdString(segments: any[], segmentId: string): string {
		if (segmentId) {
			for (const segment of segments) {
				if (segment.isActive) {
					const foundSubSegment = segment.subSegments?.find(
						(subSegment) => subSegment.id === segmentId && subSegment.isActive
					);

					if (foundSubSegment) {
						return foundSubSegment.idNumber;
					}
				}
			}
		}
		return '000';
	}
	getSubSegmentIDsString(clientId: string, generalLedgerId: string, item: any, segments: any[]): Observable<string> {
		const buildSegmentString = (segmentsData: any[]) => {
			if (!segmentsData || segmentsData.length === 0) return '';

			// Get max segmentNumber
			const maxSegment = Math.max(...segmentsData.map((s) => s.segmentNumber));

			const subSegmentIDs: string[] = [];

			for (let i = 1; i <= maxSegment; i++) {
				const segmentIdField = `segment${i}Id`;
				const segmentId = item[segmentIdField];

				subSegmentIDs.push(this.getSegmentationIdString(segmentsData, segmentId));
			}

			return subSegmentIDs.join('-');
		};

		if (segments.length === 0) {
			return this.getSubSegmentsByGeneralLedgerId(clientId, generalLedgerId).pipe(
				map((data) => buildSegmentString(data))
			);
		} else {
			return of(buildSegmentString(segments));
		}
	}

	getAllSubSegmentsByAccounts(clientId: string, gls: string[]): Observable<any[]> {
		const url = `${environment.apiUrl}${this.urlPath}/clients/${clientId}/get-sub-segments-by-accounts`;
		return this.httpClient.post<any[]>(url, gls).pipe();
	}
	getSubSegmentsYearlyBudgetByGL(clientId: string, glId: string, subSegmentIds: string[]): Observable<any[]> {
		const params = new HttpParams().set('generalLedgerId', glId).set('subSegmentIds', subSegmentIds.join(','));
		return this.httpClient.get<any>(
			`${environment.apiUrl}${this.urlPath}/clients/${clientId}/get-sub-segments-yearly-budget-by-gl`,
			{ params }
		);
	}
}
